import { useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, ArrowUpRight } from "lucide-react";
import { INSIGHTS, getInsight } from "@/constants/insights";
import useSeo from "@/hooks/useSeo";

export default function Article() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const post = getInsight(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    if (!post) navigate("/insights", { replace: true });
  }, [post, navigate]);

  useSeo(
    post
      ? {
          title: `${post.title} | Yugiq Insights`,
          description: post.excerpt,
          canonical: `https://yugiq.in/insights/${post.slug}`,
          keywords: post.keywords,
          jsonLd: {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: post.title,
            description: post.excerpt,
            datePublished: post.date,
            author: { "@type": "Organization", name: "Yugiq Technologies" },
            publisher: {
              "@type": "Organization",
              name: "Yugiq Technologies",
              url: "https://yugiq.in/",
            },
            mainEntityOfPage: `https://yugiq.in/insights/${post.slug}`,
          },
        }
      : {}
  );

  if (!post) return null;

  const related = INSIGHTS.filter((p) => p.slug !== post.slug).slice(0, 2);

  return (
    <main className="min-h-screen bg-[var(--yg-bg)] text-white" data-testid="article-page">
      {/* Top bar */}
      <div className="border-b border-[var(--yg-border)] backdrop-blur-xl bg-[rgba(7,7,13,0.72)] sticky top-0 z-40">
        <div className="max-w-[760px] mx-auto px-5 sm:px-8 h-[64px] flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="article-logo">
            <span className="w-9 h-9 rounded-[10px] overflow-hidden flex items-center justify-center">
              <img src="/logo.png" alt="Yugiq logo" className="w-full h-full object-cover" />
            </span>
            <span className="text-[15px] font-extrabold tracking-[-0.02em] text-white">Yugiq</span>
          </Link>
          <Link to="/insights" className="yg-btn yg-btn-ghost" data-testid="article-back">
            <ArrowLeft className="w-4 h-4" /> All insights
          </Link>
        </div>
      </div>

      {/* Header */}
      <div className="yg-grid-bg">
        <article className="max-w-[760px] mx-auto px-5 sm:px-8 pt-14 sm:pt-20 pb-8">
          <div className="flex items-center gap-3">
            <span className="yg-mono text-[10.5px] tracking-[0.2em] uppercase text-[var(--yg-violet-light)]">{post.category}</span>
            <span className="w-1 h-1 rounded-full bg-[var(--yg-text-dim)]" />
            <span className="yg-mono text-[10.5px] tracking-[0.16em] uppercase text-[var(--yg-text-dim)]">{post.readTime}</span>
          </div>
          <h1 className="yg-display text-[clamp(30px,5vw,52px)] mt-4 text-white leading-[1.02]">{post.title}</h1>
          <p className="mt-5 text-[16px] sm:text-[18px] leading-relaxed text-[var(--yg-text-mute)]">{post.excerpt}</p>
        </article>
      </div>

      {/* Body */}
      <article className="max-w-[760px] mx-auto px-5 sm:px-8 py-10 sm:py-14">
        <div className="space-y-9">
          {post.body.map((block, i) => (
            <section key={i} data-testid={`article-section-${i}`}>
              <h2 className="yg-h2 text-[22px] sm:text-[26px] text-white">{block.h}</h2>
              <p className="mt-3 text-[15px] sm:text-[16px] leading-[1.75] text-[var(--yg-text-mute)]">{block.p}</p>
            </section>
          ))}
        </div>

        {/* CTA */}
        <div className="yg-card p-6 sm:p-8 mt-14 relative overflow-hidden" data-testid="article-cta">
          <div aria-hidden className="absolute -top-16 -right-16 w-[240px] h-[240px] rounded-full opacity-60 pointer-events-none" style={{ background: "radial-gradient(closest-side, rgba(108,58,237,0.3), transparent 70%)" }} />
          <div className="relative">
            <h3 className="yg-h2 text-[22px] sm:text-[26px] text-white">Want this working for your business?</h3>
            <p className="mt-3 text-[14.5px] text-[var(--yg-text-mute)] max-w-[520px]">Get a free AI audit — we map your ops, spot the leverage points, and quote for exactly what you need.</p>
            <Link to="/#audit" className="yg-btn yg-btn-primary mt-6" data-testid="article-cta-btn">
              Get Free AI Audit <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        {/* Related */}
        <div className="mt-14">
          <div className="yg-mono text-[10.5px] tracking-[0.24em] uppercase text-[var(--yg-text-dim)] mb-4">Read next</div>
          <div className="grid sm:grid-cols-2 gap-4">
            {related.map((p) => (
              <Link key={p.slug} to={`/insights/${p.slug}`} className="yg-card yg-card-hover p-5 group" data-testid={`article-related-${p.slug}`}>
                <div className="flex items-center justify-between">
                  <span className="yg-mono text-[10px] tracking-[0.2em] uppercase text-[var(--yg-violet-light)]">{p.category}</span>
                  <ArrowUpRight className="w-4 h-4 text-[var(--yg-text-dim)] group-hover:text-[var(--yg-violet-light)] transition-colors" />
                </div>
                <div className="mt-3 text-white font-semibold text-[15px] leading-tight tracking-[-0.01em]">{p.title}</div>
              </Link>
            ))}
          </div>
        </div>
      </article>
    </main>
  );
}
